import React, { useCallback, useRef, useState } from 'react';
import { Close, MoreHoriz } from '@material-ui/icons';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';

import { convertToLocalTime } from '../../../../services/timeStampService';
import { joinClassNames } from '../../../../services/elementHelperService';
import Icons from '../../../../reusable/IconsStore/Icons';

import './ChatContainer.scss';

export namespace ChatContainerTemplate {
	export const chatBaseClassName = 'chat-container';
	export interface ChatContainerProps {
		mediaId: string;
		settings: any;
		mediaDetails?: any;
		onShowCreatePoll?: () => void;
		reloadData?: () => void;
		registerSocketMessageHandler?: (_handler: (_evt: MessageEvent) => void) => void;
		unregisterSocketMessageHandler?: (_handler: (_evt: MessageEvent) => void) => void;
	}
	export interface Message {
		id: string;
		message: { text: string; pinned?: boolean };
		sender: { name: string; admin?: boolean; userId: string };
		recipient: { replyMessageId?: string };
		created: string;
		blocked?: boolean;
	}

	export interface ChatMessageProps {
		message: Message;
		repliedMessage?: Message;
		// eslint-disable-next-line no-unused-vars
		onReply?: (messageId: string) => void;
		// eslint-disable-next-line no-unused-vars
		onBlockMessage?: (message: Message) => void;
		// eslint-disable-next-line no-unused-vars
		onBlockUser?: (message: Message, block?: boolean) => void;
		// eslint-disable-next-line no-unused-vars
		onTogglePinnedMessage?: (messageId: string, pinned?: boolean) => void;
		pinned?: boolean;
		replyingMessage?: boolean;
		blockedUserIds?: string[];
		pinedMessageId?: string;
	}

	export const ChatMessage: React.FC<ChatMessageProps> = ({
		message,
		repliedMessage,
		onReply,
		onBlockMessage,
		onTogglePinnedMessage,
		pinned,
		replyingMessage,
		onBlockUser,
		blockedUserIds = [],
		pinedMessageId,
	}) => {
		const { t } = useTranslation();
		const actionButtonRef = useRef<HTMLButtonElement | null>(null);
		const messageContainerRef = useRef<HTMLDivElement | null>(null);
		const [openActionMenu, setOpenActionMenu] = useState<boolean>(false);

		let foundBlockedUser = _.find(blockedUserIds, (user) => user === message.sender.userId);

		const onReplyClick = useCallback(() => {
			onReply?.(message.id);
			setOpenActionMenu(false);
		}, [message.id, onReply]);

		const onBlockMessageClick = useCallback(() => {
			onBlockMessage?.(message);
			setOpenActionMenu(false);
		}, [message, onBlockMessage]);

		const onBlockUserClick = useCallback(() => {
			onBlockUser?.(message, !foundBlockedUser);
			setOpenActionMenu(false);
		}, [foundBlockedUser, message, onBlockUser]);

		const onPinMessageClick = useCallback(() => {
			onTogglePinnedMessage?.(message.id, !message.message?.pinned);
			setOpenActionMenu(false);
		}, [message.id, message.message?.pinned, onTogglePinnedMessage]);

		return (
			<>
				<div
					ref={(ref) => (messageContainerRef.current = ref)}
					className={joinClassNames(
						`${chatBaseClassName}__message-container`,
						pinned || replyingMessage ? `${chatBaseClassName}__message--pinned` : '',
						message.sender?.admin ? `${chatBaseClassName}__message--admin` : ''
					)}
					key={message.id}
				>
					<div className={joinClassNames(`${chatBaseClassName}__message`)}>
						<div className={`${chatBaseClassName}__message-title`}>
							<div>
								{pinned && <Icons.PinIcon />}
								<span className={`${chatBaseClassName}__message-sender`}>{message.sender?.name}</span>
								<span className={`${chatBaseClassName}__message-time`}>
									{convertToLocalTime(message.created)}
								</span>
							</div>

							{pinned && (
								<IconButton
									className={`${chatBaseClassName}__message-more-btn`}
									size="small"
									onClick={onPinMessageClick}
									title={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_UNPIN_MESSAGE')}
								>
									<Close />
								</IconButton>
							)}

							{!pinned && (
								<IconButton
									className={`${chatBaseClassName}__message-more-btn`}
									size="small"
									ref={(ref) => (actionButtonRef.current = ref)}
									onClick={() => setOpenActionMenu((prev) => !prev)}
									title={t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_OPEN_ACTION_MENU')}
								>
									<MoreHoriz />
								</IconButton>
							)}
						</div>

						{repliedMessage && (
							<div className={`${chatBaseClassName}__message-quote`}>{repliedMessage.message.text}</div>
						)}
						<div
							className={`${chatBaseClassName}__message-content`}
							title={pinned ? message.message.text : undefined}
						>
							{message.message.text}
						</div>
					</div>
				</div>

				<Menu
					open={openActionMenu}
					anchorEl={actionButtonRef.current}
					onClose={() => setOpenActionMenu(false)}
					getContentAnchorEl={null}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				>
					<MenuItem onClick={onReplyClick}>
						{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_REPLY') as string}
					</MenuItem>
					<MenuItem onClick={onBlockUserClick}>
						{foundBlockedUser
							? t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_UNBLOCK_USER') as string
							: (t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_BLOCK_USER') as string)}
					</MenuItem>
					<MenuItem onClick={onBlockMessageClick}>
						{t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_BLOCK_MESSAGE') as string}
					</MenuItem>
					<MenuItem onClick={onPinMessageClick}>
						{pinedMessageId && message.id === pinedMessageId
							? t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_UNPIN_MESSAGE') as string
							: (t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_LIVE_MANAGER_PIN_MESSAGE') as string)}
					</MenuItem>
				</Menu>
			</>
		);
	};
}
